import classNames from "classnames";
import { NavLink } from "../../../../router/LocaleLink";
import { Tooltip } from "antd";
import { memo } from "react";
import { useTranslation } from "react-i18next";
import { useLocalizedPath } from "../../../../i18n/useAppLocale";
import styles from "./Menu.module.css";

export interface MenuItem {
  key: string;
  labelKey: string;
  icon?: React.ReactNode;
  external?: boolean;
  href?: () => string;
}

export interface SidebarMenuClickMeta {
  section_position: number;
  section_category: string;
}

export const Menu = memo(
  ({
    items,
    isCollapsed = false,
    onItemClick,
  }: {
    items: MenuItem[];
    isCollapsed?: boolean;
    onItemClick?: (item: MenuItem, meta: SidebarMenuClickMeta) => void;
  }) => {
    const { t } = useTranslation();
    const localize = useLocalizedPath();

    return (
      <ul className={styles.menu}>
        {items.map((item, index) => {
          const label = t(item.labelKey);
          const clickMeta: SidebarMenuClickMeta = {
            section_position: index + 1,
            section_category: items[0]?.labelKey ?? "unknown",
          };
          const handleItemClick = () => {
            onItemClick?.(item, clickMeta);
          };
          const menuContent = (
            <li
              key={item.key}
              className={classNames(styles.menuItem, {
                [styles.menuItemCollapsed]: isCollapsed,
              })}
            >
              <div
                className={classNames(styles.menuItemIcon, {
                  [styles.menuItemIconCollapsed]: isCollapsed,
                })}
              >
                {item.icon}
              </div>
              {!isCollapsed && (
                <div className={styles.menuItemLabel}>{label}</div>
              )}
            </li>
          );

          if (item.external && item.href) {
            return (
              <a
                key={item.key}
                href={item.href()}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleItemClick}
                className={
                  isCollapsed ? styles.menuLinkCollapsed : styles.menuLink
                }
              >
                {isCollapsed ? (
                  <Tooltip title={label} placement="right">
                    {menuContent}
                  </Tooltip>
                ) : (
                  menuContent
                )}
              </a>
            );
          }

          return (
            <NavLink
              to={localize(item.key)}
              key={item.key}
              onClick={handleItemClick}
              className={({ isActive }) =>
                classNames(
                  isCollapsed ? styles.menuLinkCollapsed : styles.menuLink,
                  {
                    [styles.menuLinkActive]: isActive,
                  },
                )
              }
            >
              {isCollapsed ? (
                <Tooltip title={label} placement="right">
                  {menuContent}
                </Tooltip>
              ) : (
                menuContent
              )}
            </NavLink>
          );
        })}
      </ul>
    );
  },
);
